/** Select / multi_select column option list stored in `columns[].config.options`. */

/** Ordered by visual interest — vivid hues first, neutral gray/brown last. */
export const SELECT_COLORS = [
  'blue',
  'purple',
  'pink',
  'red',
  'orange',
  'teal',
  'cyan',
  'indigo',
  'green',
  'yellow',
  'brown',
  'gray',
] as const;

export type SelectColor = (typeof SELECT_COLORS)[number];

export type SelectOption = {
  id: string;
  name: string;
  color: SelectColor;
};

export type ColumnConfigLike = Record<string, unknown> | undefined;

function newOptionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `opt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Stable id for legacy string / name-only options (same name → same id across reads). */
export function stableOptionIdFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `opt-${slug || 'unnamed'}`;
}

function defaultColorForIndex(index: number): SelectColor {
  return SELECT_COLORS[index % SELECT_COLORS.length] ?? 'gray';
}

function isSelectColor(value: unknown): value is SelectColor {
  return typeof value === 'string' && (SELECT_COLORS as readonly string[]).includes(value);
}

function normalizeOneOption(raw: unknown, index: number): SelectOption | null {
  if (typeof raw === 'string') {
    const name = raw.trim();
    if (!name) {
      return null;
    }
    return { id: stableOptionIdFromName(name), name, color: defaultColorForIndex(index) };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const name =
    typeof obj.name === 'string'
      ? obj.name.trim()
      : typeof obj.label === 'string'
        ? obj.label.trim()
        : '';
  if (!name) {
    return null;
  }
  const id =
    typeof obj.id === 'string' && obj.id.trim()
      ? obj.id.trim()
      : stableOptionIdFromName(name);
  const color = isSelectColor(obj.color) ? obj.color : defaultColorForIndex(index);
  return { id, name, color };
}

/** Read `config.options` into stable `{ id, name, color }[]` (handles legacy string[] / {name}[]). */
export function normalizeSelectOptions(config: ColumnConfigLike): SelectOption[] {
  const options = config?.options;
  if (!Array.isArray(options)) {
    return [];
  }
  const out: SelectOption[] = [];
  const seenIds = new Set<string>();
  for (let i = 0; i < options.length; i++) {
    const opt = normalizeOneOption(options[i], i);
    if (!opt) {
      continue;
    }
    let id = opt.id;
    while (seenIds.has(id)) {
      id = newOptionId();
    }
    seenIds.add(id);
    out.push({ ...opt, id });
  }
  return out;
}

export function selectOptionsFromConfig(config: ColumnConfigLike): SelectOption[] {
  return normalizeSelectOptions(config);
}

/** Match stored cell value (id preferred, then legacy label) to an option. */
export function resolveOptionByValue(
  config: ColumnConfigLike,
  value: string | null | undefined,
): SelectOption | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const options = normalizeSelectOptions(config);
  const byId = options.find((o) => o.id === value);
  if (byId) {
    return byId;
  }
  const lower = value.toLowerCase();
  const byName = options.find((o) => o.name.toLowerCase() === lower);
  if (byName) {
    return byName;
  }
  // Fallback: cell stored a name-derived slug id but the option's id has since
  // drifted (e.g. option re-added with a fresh id). Match on the slug.
  return options.find((o) => stableOptionIdFromName(o.name) === value) ?? null;
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

export function selectDisplayLabel(
  config: ColumnConfigLike,
  value: string | null | undefined,
): string {
  const opt = resolveOptionByValue(config, value);
  if (opt) {
    return opt.name;
  }
  if (!value || value === '') {
    return '';
  }
  // Hide internal ids (uuid or `opt-…` slug) when the option no longer exists.
  if (isUuidLike(value) || value.startsWith('opt-')) {
    return '';
  }
  return value;
}

export function selectDisplayColor(
  config: ColumnConfigLike,
  value: string | null | undefined,
): SelectColor | null {
  return resolveOptionByValue(config, value)?.color ?? null;
}

export type EnsureOptionResult = {
  config: Record<string, unknown>;
  option: SelectOption;
  created: boolean;
};

/** Find option by name (case-insensitive) or append a new one. */
export function ensureOption(
  config: ColumnConfigLike,
  name: string,
): EnsureOptionResult {
  const trimmed = name.trim();
  if (!trimmed) {
    throw new Error('Option name is required');
  }
  const base = config && typeof config === 'object' ? { ...config } : {};
  const options = normalizeSelectOptions(base);
  const existing = options.find((o) => o.name.toLowerCase() === trimmed.toLowerCase());
  if (existing) {
    return { config: { ...base, options }, option: existing, created: false };
  }
  const option: SelectOption = {
    id: newOptionId(),
    name: trimmed,
    color: defaultColorForIndex(options.length),
  };
  const nextOptions = [...options, option];
  return {
    config: { ...base, options: nextOptions },
    option,
    created: true,
  };
}

export function configWithSelectOptions(
  config: ColumnConfigLike,
  options: SelectOption[],
): Record<string, unknown> {
  const base = config && typeof config === 'object' ? { ...config } : {};
  return { ...base, options };
}

/** Normalize options array for schema PUT (stable ids, valid colors). */
export function normalizeSelectOptionsForSchema(
  config: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!config) {
    return {};
  }
  const options = normalizeSelectOptions(config);
  if (options.length === 0 && !('options' in config)) {
    return config;
  }
  return { ...config, options };
}
